import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, MapPin } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { DASHBOARD, PROPERTY } from "../constants/testIds";

export default function DashboardPage() {
  const { user, logout, authAxios } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState(user?.language || "en");
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    authAxios.get("/user/dashboard")
      .then(({ data }) => {
        setProperties(data.properties);
        setLanguage(data.user?.language || "en");
        setUserData(data.user);
      })
      .catch(() => logout())
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = (en, it) => language === "it" && it ? it : en;
  const getPriceDisplay = (p) => {
    if (p.price_visibility === "hidden") return null;
    if (p.price_visibility === "on_request") return "Price on Request";
    if (p.price) return `${p.currency} ${Number(p.price).toLocaleString()}`;
    return "Price on Request";
  };

  // Welcome message — personalised or fallback
  const welcomeTitle = language === "it"
    ? (userData?.welcome_title_it || userData?.welcome_title_en || "Benvenuto.")
    : (userData?.welcome_title_en || "Welcome.");
  const welcomeMessage = language === "it"
    ? (userData?.welcome_message_it || userData?.welcome_message_en || "Abbiamo selezionato per Lei una raccolta di opportunità dedicate.")
    : (userData?.welcome_message_en || "A curated selection of opportunities has been prepared exclusively for you.");

  if (loading) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="w-px h-16 bg-[#C8102E] animate-pulse" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid={DASHBOARD.container}>
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-8 py-5 flex items-center justify-between border-b border-zinc-900/60 bg-[#050505]/90 backdrop-blur-sm">
        <div>
          <img
            src="https://customer-assets.emergentagent.com/job_investment-portal-38/artifacts/5nykqmge_Green%20and%20Gold%20Professional%20Letter%20A%20Logo.png"
            alt="Scarlett Properties"
            className="h-10 w-auto"
            style={{ mixBlendMode: "screen" }}
          />
        </div>
        <div className="flex items-center gap-6">
          <button
            onClick={() => setLanguage(l => l === "en" ? "it" : "en")}
            data-testid={DASHBOARD.languageSwitcher}
            className="text-[10px] tracking-[0.3em] text-white hover:text-white uppercase transition-colors"
          >
            {language === "en" ? "IT" : "EN"}
          </button>
          <div className="w-px h-4 bg-zinc-800" />
          <span className="text-xs text-white tracking-wider font-light" data-testid={DASHBOARD.welcomeText}>
            {user?.first_name} {user?.last_name}
          </span>
          <button
            onClick={() => { logout(); navigate("/"); }}
            data-testid={DASHBOARD.logoutBtn}
            className="text-zinc-600 hover:text-white transition-colors"
          >
            <LogOut size={14} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="pt-28 pb-24 px-8 max-w-7xl mx-auto">
        {/* Welcome Message */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <div className="w-6 h-px bg-[#C8102E] mb-8" />
          <h1
            className="font-serif text-4xl sm:text-5xl font-light text-white tracking-tight mb-5"
            data-testid="welcome-title"
          >
            {welcomeTitle}
          </h1>
          <p className="text-sm text-white/60 font-light leading-relaxed max-w-2xl mb-4" data-testid="welcome-message">
            {welcomeMessage}
          </p>
          <p className="text-[9px] tracking-[0.3em] text-white/20 uppercase font-light">
            {t(
              "Information contained within this portal is confidential and intended solely for the recipient.",
              "Le informazioni contenute in questo portale sono riservate e destinate esclusivamente al destinatario."
            )}
          </p>
        </motion.div>

        {/* Empty State */}
        {properties.length === 0 && (
          <div className="flex flex-col items-center justify-center py-32" data-testid={DASHBOARD.emptyState}>
            <div className="w-px h-12 bg-zinc-800 mb-8" />
            <p className="text-white text-sm tracking-wider font-light">
              {t("No opportunities assigned yet.", "Nessuna opportunità assegnata.")}
            </p>
            <p className="text-white/60 text-xs tracking-wider mt-2 font-light">
              {t("Your advisor will assign curated opportunities.", "Il tuo advisor assegnerà opportunità selezionate.")}
            </p>
          </div>
        )}

        {/* Property Grid */}
        {properties.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8" data-testid={DASHBOARD.propertyGrid}>
            {properties.map((prop, i) => (
              <motion.div
                key={prop.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 + 0.3, duration: 0.6 }}
                data-testid={PROPERTY.card}
              >
                <PropertyCard prop={prop} language={language} navigate={navigate} />
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function PropertyCard({ prop, language, navigate }) {
  const name = language === "it" && prop.name_it ? prop.name_it : prop.name_en;
  const desc = (language === "it" && prop.description_it ? prop.description_it : prop.description_en || "").substring(0, 150);

  // Replaced nested ternary with explicit if/else for readability
  let priceDisplay = null;
  if (prop.price_visibility === "on_request") {
    priceDisplay = "Price on Request";
  } else if (prop.price_visibility !== "hidden" && prop.price) {
    priceDisplay = `${prop.currency} ${Number(prop.price).toLocaleString()}`;
  }

  const locationText = useMemo(
    () => [prop.city, prop.country].filter(Boolean).join(", "),
    [prop.city, prop.country]
  );

  return (
    <div
      className="group cursor-pointer"
      onClick={() => navigate(`/property/${prop.id}`)}
    >
      {/* Image */}
      <div className="relative overflow-hidden aspect-[4/3] bg-zinc-900 mb-5">
        {prop.featured_image ? (
          <img
            src={prop.featured_image}
            alt={name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
            <div className="w-8 h-px bg-zinc-700" />
          </div>
        )}
        {prop.category_name && (
          <div className="absolute top-4 left-4">
            <span className="text-[9px] tracking-[0.3em] text-white/80 uppercase bg-black/50 px-2 py-1 font-light backdrop-blur-sm">
              {prop.category_name}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <MapPin size={10} strokeWidth={1.5} className="text-white" />
          <span className="text-[10px] tracking-[0.2em] text-white uppercase font-light">
            {locationText}
          </span>
        </div>
        <h3 className="font-serif text-xl font-light text-white tracking-wide mb-2 group-hover:text-white transition-colors">
          {name}
        </h3>
        {priceDisplay && (
          <p className="text-[10px] tracking-[0.25em] text-[#C8102E] uppercase font-light mb-3">
            {priceDisplay}
          </p>
        )}
        {desc && (
          <p className="text-white text-xs leading-relaxed font-light mb-4 line-clamp-2">{desc}</p>
        )}
        <button
          data-testid={PROPERTY.viewBtn}
          className="text-[9px] tracking-[0.35em] text-white/60 uppercase border-b border-white/20 hover:text-white hover:border-white/50 transition-all duration-300 pb-px font-light"
        >
          View Opportunity
        </button>
      </div>
    </div>
  );
}
